/**
 * Структурная экстракция push-payload для Service Worker.
 *
 * Источник пуша (FCM или нативный Web Push) неизвестен и не определяется — важна
 * только структура: полезные данные лежат либо в `data`-обёртке, либо напрямую.
 * Разворачиваем обёртку, если она есть; иначе отдаём payload как есть. Это покрывает
 * FCM legacy, FCM v1 (`{message:{data}}`) и любые врапперы с двойной вложенностью,
 * не опираясь на транспортные маркеры (`from`/`fcmMessageId`), которые могут
 * отсутствовать у части каналов.
 */

export type PushData = Record<string, unknown> | null | undefined;

/** Разварапить двойную вложенность, когда payload лежит в `data.data` как JSON-строка. */
function normalizeData(data: unknown): PushData {
    if (data && typeof data === 'object') {
        const inner = (data as Record<string, unknown>).data;

        if (typeof inner === 'string') {
            try {
                const parsed = JSON.parse(inner);

                if (parsed && typeof parsed === 'object') {
                    return parsed as PushData;
                }
            } catch {
                // не JSON — отдаём data как есть
            }
        }
    }

    return data as PushData;
}

/**
 * Вернуть полезные данные пуша: разворачивает `data`-обёртку (FCM legacy, FCM v1,
 * произвольный враппер) либо отдаёт плоский payload как есть.
 */
export function unwrapPayload(payload: unknown): PushData {
    if (typeof payload !== 'object' || payload === null) {
        return payload as PushData;
    }

    const record = payload as Record<string, unknown>;

    // FCM v1 server API: { message: { data: {...} } }
    const message = record.message;

    if (message && typeof message === 'object') {
        const messageData = (message as Record<string, unknown>).data;

        if (messageData && typeof messageData === 'object') {
            return normalizeData(messageData);
        }
    }

    // FCM legacy / произвольный враппер: { data: {...} }
    if (record.data && typeof record.data === 'object') {
        return normalizeData(record.data);
    }

    // плоский payload: поля напрямую (ru-title, ru-text, ...)
    return payload as PushData;
}
